import React, { useState, useEffect } from 'react';
import { X, Plus, GripVertical, Trash2 } from 'lucide-react';

const StageEditModal = ({ isOpen, onClose, stages, onSave, projectId }) => {
  // Normalize stages - convert objects to strings if needed
  const normalizeStages = (stageArray) => {
    return stageArray.map(stage => {
      if (typeof stage === 'string') {
        return stage;
      } else if (stage && typeof stage === 'object') {
        // Stage is an object with {id, name, description, etc.}
        return stage.name || stage;
      }
      return stage;
    });
  };

  const [editedStages, setEditedStages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Normalize stages when they change
      const normalized = normalizeStages(stages);
      setEditedStages(normalized);
      setEditingIndex(null);
      setEditingValue('');
    }
  }, [isOpen, stages]);

  const handleDragStart = (e, index) => {
    console.log('🎯 Drag started:', index);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index); // Required for Firefox
    
    // Create a custom drag image showing the full stage item
    const dragElement = e.target.closest('.stage-item');
    if (dragElement) {
      // Clone the element for the drag preview
      const clone = dragElement.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.top = '-9999px';
      clone.style.width = dragElement.offsetWidth + 'px';
      clone.style.opacity = '0.8';
      clone.style.backgroundColor = 'white';
      clone.style.border = '2px solid #3b82f6';
      clone.style.borderRadius = '0.5rem';
      document.body.appendChild(clone);
      
      e.dataTransfer.setDragImage(clone, dragElement.offsetWidth / 2, 20);
      
      // Clean up the clone after drag starts
      setTimeout(() => document.body.removeChild(clone), 0);
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('📍 Drag over:', index, 'dragging:', draggedIndex);
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback
    if (draggedIndex !== null && draggedIndex !== index) {
      e.currentTarget.classList.add('border-blue-400', 'bg-blue-50');
    }
  };
  
  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('💧 Drop at:', dropIndex, 'from:', draggedIndex);
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newStages = [...editedStages];
    const [removed] = newStages.splice(draggedIndex, 1);
    newStages.splice(dropIndex, 0, removed);
    
    console.log('✅ Reordered stages:', newStages);
    setEditedStages(newStages);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    console.log('🏁 Drag ended');
    setDraggedIndex(null);
  };

  const handleAddStage = () => {
    if (!newStageName.trim()) return;
    
    // Check for duplicates
    if (editedStages.includes(newStageName.trim())) {
      alert('A stage with this name already exists');
      return;
    }

    setEditedStages([...editedStages, newStageName.trim()]);
    setNewStageName('');
  };

  const handleRemoveStage = (index) => {
    if (editedStages.length <= 2) {
      alert('You must have at least 2 stages');
      return;
    }

    const stageName = editedStages[index];
    const confirmed = window.confirm(
      `Are you sure you want to remove "${stageName}"?\n\nTasks in this stage will be moved to "To Do".`
    );
    
    if (confirmed) {
      const newStages = editedStages.filter((_, i) => i !== index);
      setEditedStages(newStages);
    }
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditingValue(editedStages[index]);
  };

  const handleEditChange = (value) => {
    setEditingValue(value);
  };

  const handleFinishEdit = (index) => {
    if (!editingValue.trim()) {
      // Reset to original value if empty
      setEditingIndex(null);
      setEditingValue('');
      return;
    }
    
    // Check for duplicates (excluding current stage)
    const otherStages = editedStages.filter((_, i) => i !== index);
    if (otherStages.includes(editingValue.trim())) {
      alert('A stage with this name already exists');
      setEditingIndex(null);
      setEditingValue('');
      return;
    }

    // Update the stage
    const newStages = [...editedStages];
    newStages[index] = editingValue.trim();
    setEditedStages(newStages);
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedStages);
      onClose();
    } catch (error) {
      console.error('Failed to save stages:', error);
      
      // Provide more detailed error messages
      if (error.response?.status === 403) {
        alert('You do not have permission to edit project stages.');
      } else if (error.response?.status === 404) {
        alert('Project not found.');
      } else if (error.response?.status === 400) {
        alert(error.response?.data?.detail || 'Invalid stage configuration. Please ensure you have at least 2 stages.');
      } else {
        alert('Failed to save stages. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const normalized = normalizeStages(stages);
    setEditedStages(normalized);
    setNewStageName('');
    onClose();
  };

  if (!isOpen) return null;

  // Debug logging
  console.log('StageEditModal rendering:', { isOpen, stages, editedStages, projectId });

  // Safety check - if stages are empty or invalid, show error
  if (!stages || stages.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">No Stages Available</h2>
            <p className="text-gray-600 mb-4">This project doesn&apos;t have any stages configured yet.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Edit Task Stages</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Current Stages */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Current Stages</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex">
                <div className="text-blue-600 text-xs">
                  <p>
                    <strong>Note:</strong> When you rename or reorder stages, all tasks will be automatically moved to maintain their workflow position. Tasks in deleted stages will be moved to the first stage.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Drag stages to reorder them. Click stage names to rename.
            </p>
            
            <div className="space-y-2">
              {editedStages.map((stage, index) => (
                <div
                  key={`stage-${index}`}
                  className={`stage-item flex items-center space-x-3 p-3 border border-gray-200 rounded-lg bg-white transition-all ${
                    draggedIndex === index ? 'opacity-50 border-blue-400' : 'hover:border-gray-300'
                  }`}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  {/* Drag Handle - ONLY this part is draggable */}
                  <div 
                    className="text-gray-400 cursor-move hover:text-gray-600 flex-shrink-0"
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <GripVertical className="w-5 h-5" />
                  </div>

                  {/* Stage Name - Editable */}
                  {editingIndex === index ? (
                    <input
                      type="text"
                      value={editingValue}
                      onChange={(e) => handleEditChange(e.target.value)}
                      onBlur={() => handleFinishEdit(index)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleFinishEdit(index);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      className="flex-1 bg-gray-50 border-none outline-none text-gray-900 font-medium focus:bg-gray-100 rounded px-2 py-1"
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={() => handleStartEdit(index)}
                      className="flex-1 text-gray-900 font-medium cursor-pointer hover:bg-gray-50 rounded px-2 py-1"
                    >
                      {stage}
                    </div>
                  )}

                  {/* Task Count Badge */}
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                    Stage {index + 1}
                  </span>

                  {/* Delete Button */}
                  {editedStages.length > 2 && (
                    <button
                      onClick={() => handleRemoveStage(index)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Remove Stage"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add New Stage */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Add New Stage</h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddStage()}
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                placeholder="Enter stage name..."
              />
              <button
                onClick={handleAddStage}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t">
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || editedStages.length < 2}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageEditModal;